import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getSchedule,
  saveSchedule as localSaveSchedule,
  toggleExercise as localToggleExercise,
  getTodayCompletions,
} from '../utils/scheduleData';
import {
  fsGetSchedule,
  fsSaveSchedule,
  fsToggleExercise,
  fsGetTodayCompletions,
} from '../services/firestoreDb';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function initCompletions() {
  const result = {};
  DAYS.forEach((d) => {
    result[d] = getTodayCompletions(d);
  });
  return result;
}

export function useSchedule() {
  const { user } = useAuth();
  const uid = user?.id;

  const [schedule, setSchedule] = useState(() => getSchedule());
  const [completions, setCompletions] = useState(() => initCompletions());

  useEffect(() => {
    if (!uid) return;
    // Fetch schedule from Firestore
    fsGetSchedule(uid)
      .then((data) => {
        if (data !== null) setSchedule(data);
      })
      .catch(() => {});

    // Fetch today's completions for every day
    Promise.all(DAYS.map((d) => fsGetTodayCompletions(uid, d)))
      .then((results) => {
        const hasData = results.some((r) => r !== null);
        if (hasData) {
          const merged = {};
          DAYS.forEach((d, i) => {
            merged[d] = results[i] !== null ? results[i] : completions[d];
          });
          setCompletions(merged);
        }
      })
      .catch(() => {});
    // intentionally omit completions from deps — we only want to merge once on uid change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const saveSchedule = useCallback(
    async (newSchedule) => {
      localSaveSchedule(newSchedule);
      setSchedule(newSchedule);
      if (uid) {
        await fsSaveSchedule(uid, newSchedule).catch(() => {});
      }
    },
    [uid]
  );

  const toggleExercise = useCallback(
    async (dayKey, exercise) => {
      const updated = localToggleExercise(dayKey, exercise);
      setCompletions((prev) => ({ ...prev, [dayKey]: updated }));
      if (uid) {
        await fsToggleExercise(uid, dayKey, exercise).catch(() => {});
      }
      return updated;
    },
    [uid]
  );

  return { schedule, setSchedule, completions, setCompletions, saveSchedule, toggleExercise };
}
